import { store } from 'modules/Editor/store';
import { selectWidget, selectWidgets } from 'modules/Editor/store/infographSelector';
import { ActiveWidgets } from 'modules/Editor/store/widgetControlSlice';
import { WidgetPosition, WidgetType } from 'types/widget.types';
import { ONE_ONE_RATIO_SHAPES } from 'widgets/BasicShapeWidget/BasicShapeWidget.config';
import { BasicShapeType, BasicShapeWidgetData } from 'widgets/BasicShapeWidget/BasicShapeWidget.types';
import { FrameShape } from 'widgets/ImageWidget/Frame';
import { ImageWidgetData } from 'widgets/ImageWidget/ImageWidget.types';
import { getWidgetTypeFromId } from 'widgets/Widget.helpers';
import { isWidgetWrapping } from 'widgets/WidgetLayout.helpers';

import { OnResizeStartEvent } from '../BoundingBox.types';
import { MAX_NUM_GRAPHIC, MAX_NUM_MEMBERS, MAX_NUM_TEXT } from './responsiveGroup.config';
import { FixedWidget, ResponsiveShape, ResponsiveShapeCell, ResponsiveTextWidget } from './responsiveGroup.types';

/**
 * Returns an array of text widgets that are wrapped by the shape widget
 * If no text widgets are wrapped by the shape widget, then an empty array is returned
 *
 * @param widgets - Array of text widget elements
 * @param shapeSize - The size of the shape widget
 *
 * @returns Array of text widget elements wrapped by the shape widget
 */
const getWidgetsWrappedByShape = (
  widgets: HTMLElement[],
  shapeSize: { topPx: number; leftPx: number; widthPx: number; heightPx: number },
) => {
  const textWidgetsWrappedByShape: HTMLElement[] = [];

  // find all the text widgets that are contained by the shape widget in the group
  widgets.forEach((widgetEl: HTMLElement) => {
    const textWidgetTopPx = parseFloat(widgetEl.style.top);
    const textWidgetWidthPx = parseFloat(widgetEl.style.width);
    const textWidgetLeftPx = parseFloat(widgetEl.style.left);
    const textWidgetHeightPx = widgetEl.clientHeight;

    const textSize = {
      topPx: textWidgetTopPx,
      leftPx: textWidgetLeftPx,
      widthPx: textWidgetWidthPx,
      heightPx: textWidgetHeightPx,
    };

    const isWrapping = isWidgetWrapping(shapeSize, textSize);
    if (isWrapping) textWidgetsWrappedByShape.push(widgetEl);
  });

  return textWidgetsWrappedByShape;
};

/**
 * Check if a text widget is wrapped by another text widget
 * (in that case it won't have a gap below it that needs to be kept when resizing)
 *
 * @param textWidgets - Array of text widget elements
 * @param textWidgetEl - The text widget element to check
 * @param idx - The index of the text widget element in the textWidgets array
 */
const isTextWidgetWrappedByOtherTextWidget = (
  textWidgets: HTMLElement[],
  textWidgetEl: HTMLElement,
  idx: number,
): boolean => {
  // no bottom padding gap in this case
  let isOverlappingTextWidget = false;
  const textWidgetTopPx = parseFloat(textWidgetEl.style.top);

  // check if it's completely wrapped by another text widget - in that case we don't need it for the height calculation later
  for (let i = 0; i < textWidgets.length; i++) {
    if (i === idx) continue;

    const otherTextWidgetEl = textWidgets[i];
    const otherTextWidgetTop = parseFloat(otherTextWidgetEl.style.top);
    const otherTextWidgetBottom = otherTextWidgetTop + otherTextWidgetEl.clientHeight;

    // if it's completely wrapped by another text widget
    // doesn't have to be width wise, just height wise
    const isTopPxGreater = textWidgetTopPx > otherTextWidgetTop;
    const isBottomPxLess = textWidgetTopPx + textWidgetEl.clientHeight < otherTextWidgetBottom;
    const isCompletelyOverlapped = isTopPxGreater && isBottomPxLess;

    if (isCompletelyOverlapped) {
      isOverlappingTextWidget = true;
      break;
    }
  }

  return isOverlappingTextWidget;
};

/**
 * Finds the top and bottom padding of the shape widget
 * (padding is later kept while resizing the shape)
 *
 * The top padding is the distance from the top of the shape to the top of the first text widget
 * The bottom padding is the distance from the bottom of the shape to the bottom of the last text widget
 *
 * @param widgetsWrappedByShape - Array of text widget elements wrapped by the shape widget
 * @param shapeSize - The size of the shape widget
 *
 * @returns { topPadding: number, bottomPadding: number }
 */
export const findShapeWidgetPadding = (
  widgetsWrappedByShape: HTMLElement[],
  shapeSize: { topPx: number; leftPx: number; widthPx: number; heightPx: number },
) => {
  let topPx = parseFloat(widgetsWrappedByShape[0].style.top);
  let bottomPx = parseFloat(widgetsWrappedByShape[0].style.top) + widgetsWrappedByShape[0].clientHeight;
  let leftPx = parseFloat(widgetsWrappedByShape[0].style.left);
  let rightPx = parseFloat(widgetsWrappedByShape[0].style.left) + widgetsWrappedByShape[0].clientWidth;

  // find the highest top position and lowest bottom position of the text widgets wrapped by the shape
  widgetsWrappedByShape.forEach((textWidgetEl: HTMLElement) => {
    const textWidgetTopPx = parseFloat(textWidgetEl.style.top);
    const textWidgetBottomPx = textWidgetTopPx + textWidgetEl.clientHeight;
    const textWidgetLeftPx = parseFloat(textWidgetEl.style.left);
    const textWidgetRightPx = textWidgetLeftPx + textWidgetEl.clientWidth;

    if (textWidgetTopPx < topPx) topPx = textWidgetTopPx;
    if (textWidgetBottomPx > bottomPx) bottomPx = textWidgetBottomPx;
    if (textWidgetLeftPx < leftPx) leftPx = textWidgetLeftPx;
    if (textWidgetRightPx > rightPx) rightPx = textWidgetRightPx;
  });

  // cannot be negative - just in case
  const topPadding = Math.max(topPx - shapeSize.topPx, 0);
  const bottomPadding = Math.max(shapeSize.topPx + shapeSize.heightPx - bottomPx, 0);
  const leftPadding = Math.max(leftPx - shapeSize.leftPx, 0);
  const rightPadding = Math.max(shapeSize.leftPx + shapeSize.widthPx - rightPx, 0);

  return { topPadding, bottomPadding, leftPadding, rightPadding };
};

/**
 * Sort the list of widget elements by their top position - highest position to lowest position
 * Obs: sorts the array in place
 *
 * @param widgets - Array of widget elements
 *
 * @returns same array of widget elements sorted by their top position
 */
export const sortByTopPxInPlace = (widgets: HTMLElement[]) => {
  return widgets.sort((a: HTMLElement, b: HTMLElement) => {
    const aTopPx = parseFloat(a.style.top);
    const bTopPx = parseFloat(b.style.top);

    return aTopPx - bTopPx;
  });
};

/**
 * Sort the list of widget elements by their top position - leftmost position to rightmost position
 * Obs: sorts the array in place
 *
 * @param widgets - Array of widget elements
 *
 * @returns same array of widget elements sorted by their left position
 */
export const sortByLeftPxInPlace = (widgets: WidgetPosition[]) => {
  return widgets.sort((a: WidgetPosition, b: WidgetPosition) => {
    const aLeftPx = a.leftPx;
    const bLeftPx = b.leftPx;

    return aLeftPx - bLeftPx;
  });
};

/**
 * Get the adjacent widgets to the left and right of the current widget
 *
 * @param widget
 * @param widgets
 * @returns left and right adjacent widget ids
 */
export const getAdjacentWidgets = (
  widget: HTMLElement,
  widgets: HTMLElement[],
): { left: string[]; right: string[] } => {
  const left: string[] = [];
  const right: string[] = [];

  if (!widget) return { left, right };

  const widgetLeft = parseFloat(widget.style.left);
  const widgetRight = widgetLeft + widget.clientWidth;

  if (typeof widgetLeft !== 'number' || typeof widgetRight !== 'number') return { left, right };

  widgets.forEach((widgetEl: HTMLElement) => {
    if (widgetEl === widget) return;

    const elLeft = parseFloat(widgetEl.style.left);
    const elRight = elLeft + widgetEl.clientWidth;
    if (typeof elLeft !== 'number' || typeof elRight !== 'number') return;

    if (elRight <= widgetLeft) {
      left.push(widgetEl.id);
    }

    if (elLeft >= widgetRight) {
      right.push(widgetEl.id);
    }
  });

  return { left, right };
};

/**
 * Compute the minimum allowed width when resizing for a responsive group
 *
 * @param responsiveShape
 * @param zoom
 * @returns
 */
export const computeResponsiveGroupMinWidth = (responsiveShape: ResponsiveShape, zoom: number) => {
  const MARGIN = 5;
  const OCTAGON_CONSTANT = 0.707;
  const { shapeId, shapeEl, topPadding, bottomPadding } = responsiveShape;
  if (!shapeEl) return;
  if (getWidgetTypeFromId(shapeId) !== WidgetType.BasicShape) return;

  const widthPx = parseFloat(shapeEl.style.width);
  const heightPx = parseFloat(shapeEl.style.height);

  // only applies to 1:1 shapes
  if (widthPx !== heightPx) return;

  const widget = selectWidget(shapeId)(store.getState()) as BasicShapeWidgetData;
  if (!widget) return;

  const { type } = widget;

  if (!ONE_ONE_RATIO_SHAPES.includes(type)) return;

  const contentHeightPx = heightPx - topPadding - bottomPadding;
  // Redistribute the content area to a square that will fit within the shape
  let minWidth = 0;
  switch (type) {
    case BasicShapeType.Ellipse: {
      const contentWidthPx = heightPx / Math.sqrt(2);
      const area = contentHeightPx * contentWidthPx;
      const minContentWidth = Math.sqrt(area);
      minWidth = Math.sqrt(2) * minContentWidth;
      break;
    }
    case BasicShapeType.Octagon: {
      const contentWidthPx = OCTAGON_CONSTANT * widthPx;
      const area = contentHeightPx * contentWidthPx;
      const minContentWidth = Math.sqrt(area);
      minWidth = minContentWidth / OCTAGON_CONSTANT;
      break;
    }
  }

  return (minWidth + MARGIN) * zoom;
};

/**
 * Processes the text widgets in the group to determine the padding between them
 * Used to populate ResponsiveGroup._textWidgets object.
 *
 * @param sortedWidgetsByTop All sorted widgets in group
 * @param groupPos Position + dimension of the group
 * @param skipIds Ids to skip over when determining the widgets below the text widget
 * @returns { [key: string]: ResponsiveTextWidget }f
 */
export const processTextWidgets = (
  sortedWidgets: {
    all: HTMLElement[];
    text: HTMLElement[];
  },
  groupPos: { leftPx: number; widthPx?: number },
  skipIds: string[],
): { [key: string]: ResponsiveTextWidget } => {
  const { leftPx: groupLeftPx, widthPx: groupWidthPx } = groupPos;
  const responsiveTextWidgets: { [key: string]: ResponsiveTextWidget } = {};
  const sortedTextByTop = sortedWidgets.text;

  const isSingleTextWidget = sortedTextByTop.length === 1;
  const visitedTextWidgets: HTMLElement[] = [];
  let textWidgetIdx = 0;
  sortedWidgets.all.forEach((widgetEl: HTMLElement, idx: number) => {
    if (!widgetEl) return;

    const widgetId = widgetEl.id;
    const widgetType = getWidgetTypeFromId(widgetId);
    const isText = widgetType === WidgetType.Text;

    if (isText) {
      // Mark as visited - for calculating padding to the next non-text widget
      visitedTextWidgets.push(widgetEl);

      const isLast = textWidgetIdx === sortedTextByTop.length - 1;
      const nextWidget = sortedTextByTop[textWidgetIdx + 1];
      textWidgetIdx++;

      // if the text widget is completely overlapped by another text widget
      const isOverlappingTextWidget = isTextWidgetWrappedByOtherTextWidget(sortedTextByTop, widgetEl, idx);

      // Compute padding from the group bounding box on the left and right
      const textWidgetLeftPx = parseFloat(widgetEl.style.left);
      const paddingLeft = textWidgetLeftPx - groupLeftPx;
      const paddingRight =
        typeof groupWidthPx === 'number' ? groupWidthPx - paddingLeft - widgetEl.clientWidth : undefined;

      // in this case, there is no padding to the text widget below this text widget
      const hasNoPaddingBottom = isSingleTextWidget || isLast || isOverlappingTextWidget || !nextWidget;
      if (hasNoPaddingBottom) {
        responsiveTextWidgets[widgetId] = {
          id: widgetId,
          paddingBottom: 0,
          isOverlappingTextWidget,
          el: widgetEl,
          originalWidth: widgetEl.clientWidth,
          originalLeftPx: textWidgetLeftPx,
          paddingLeft,
          paddingRight,
          widgetsBelow: {},
        };
        return;
      }

      // find the padding / gap to the text widget below this text widget
      const nextWidgetTop = parseFloat(nextWidget.style.top);
      const currentTextWidgetBottom = parseFloat(widgetEl.style.top) + widgetEl.clientHeight;
      // (cannot be negative)
      const paddingBottom = Math.max(nextWidgetTop - currentTextWidgetBottom, 0);

      responsiveTextWidgets[widgetId] = {
        id: widgetId,
        el: widgetEl,
        paddingBottom,
        isOverlappingTextWidget: false,
        originalWidth: widgetEl.clientWidth,
        originalLeftPx: textWidgetLeftPx,
        paddingLeft,
        paddingRight,
        widgetsBelow: {},
      };
      return;
    }

    // Non-text widgets - determine if there are any text widgets above it
    // Haven't visited any text widgets, don't need to process
    if (visitedTextWidgets.length === 0) return;
    if (skipIds.includes(widgetId)) return;

    // Create `widgetsBelow` array to determine the non-text widgets below current
    // Loop through the visited widgets, check if there are any above the current widget.
    // If yes, calculate the padding from the bottom of the text widget to the top of the current widget
    const currentWidgetTop = parseFloat(widgetEl.style.top);

    // visitedTextWidgets should be sorted by top position, start with lowest text widget at last index
    for (let i = visitedTextWidgets.length - 1; i >= 0; i--) {
      const textWidgetEl = visitedTextWidgets[i];
      if (!textWidgetEl) continue;

      const textWidgetId = textWidgetEl.id;
      const textWidgetTopPx = parseFloat(textWidgetEl.style.top);
      const textWidgetBottomPx = textWidgetTopPx + textWidgetEl.clientHeight;
      if (currentWidgetTop >= textWidgetBottomPx) {
        const paddingBottom = currentWidgetTop - textWidgetBottomPx;
        responsiveTextWidgets[textWidgetId].widgetsBelow[widgetId] = {
          padding: paddingBottom,
          el: widgetEl,
        };
        break;
      }
    }
  });

  return responsiveTextWidgets;
};

/**
 * Process graphic widgets in the group to determine which widgets are fixed
 * Fixed widgets will maintain their dimensions and adjacent widgets will adjust accordingly
 * Used to populate ResponsiveGroup._fixedWidgets object.
 *
 * @param graphicWidgets
 * @param allTextWidgets
 * @param groupPos
 * @returns
 */
export const processGraphicWidgets = (
  graphicWidgets: HTMLElement[],
  allTextWidgets: HTMLElement[],
  groupPos: { widthPx: number; leftPx: number },
): { [key: string]: FixedWidget } => {
  const fixedWidgets: { [key: string]: FixedWidget } = {};
  // create fixed widgets array - these widgets will maintin dimensions and adjacent widgets will adjust accordingly
  graphicWidgets.forEach((graphicWidgetEl: HTMLElement) => {
    if (!graphicWidgetEl) return;

    const graphicWidgetId = graphicWidgetEl.id;

    // Compute adjacent widgets - will need to determine
    // Note: will only support all text being on either side, (either leftWidgets or rightWidgets will be empty)
    const { left: leftWidgets, right: rightWidgets } = getAdjacentWidgets(graphicWidgetEl, allTextWidgets);

    // Check whether the widget should be fixed or not
    const isIconWidget = [WidgetType.Icon, WidgetType.LineIcon].includes(getWidgetTypeFromId(graphicWidgetId));
    const isImageWidget = getWidgetTypeFromId(graphicWidgetId) === WidgetType.Image;
    let isFixed = false;

    // All icon widgets are fixed since they must maintain a certain aspect ratio for tight-fitting bb
    if (isIconWidget) {
      isFixed = true;
    }

    // Single column image is not fixed
    const imageWidgetData = selectWidget(graphicWidgetId)(store.getState()) as ImageWidgetData;
    if (isImageWidget) {
      if (leftWidgets.length > 0 || rightWidgets.length > 0) {
        isFixed = true;
      } else if (imageWidgetData?.frame !== FrameShape.None) {
        // Single column image widget should stay fixed if has an image frame
        isFixed = true;
      }
    }

    if (!isFixed) return;

    // Compute the padding percentage of total group width from the left edge to center of this widget
    // Used to keep relative positioning on resize
    // Future CI: Update for different alignments, currently only works well for centred or mostly centred widgets
    let paddingLeftPct = 0;
    if (leftWidgets.length === 0 && rightWidgets.length === 0) {
      const paddingLeftFromBg =
        parseFloat(graphicWidgetEl.style.left) - groupPos.leftPx + graphicWidgetEl.clientWidth / 2;
      paddingLeftPct = Math.max(paddingLeftFromBg, 0) / groupPos.widthPx;
    }

    fixedWidgets[graphicWidgetId] = {
      id: graphicWidgetId,
      el: graphicWidgetEl,
      originalWidth: graphicWidgetEl.clientWidth,
      leftWidgets,
      rightWidgets,
      paddingLeftPct,
    };
  });
  return fixedWidgets;
};

/**
 * Process the shapes in a responsive group to determine the padding between the shapes and widgets it wraps
 * Finds bg shape (wraps all widgets) if any.
 * Finds fixed text widgets - text widgets wrapped by a 1:1 shape
 *
 * @param shapeWidgets
 * @param allTextWidgets
 * @param fixedWidgets
 * @param fixedWidgetsMap
 * @returns
 */
export const processInternalShapeWidgets = (
  shapeWidgets: HTMLElement[],
  allTextWidgets: HTMLElement[],
  fixedWidgets: HTMLElement[],
  fixedWidgetsMap: { [key: string]: FixedWidget },
) => {
  let bgShapeWidgetEl: HTMLElement | null = null;
  let bgShapeId = '';
  let bgShapeSize = { topPx: 0, leftPx: 0, widthPx: 0, heightPx: 0 };
  const cellShapeWidgets: { [key: string]: ResponsiveShapeCell } = {};

  shapeWidgets.forEach((shapeWidgetEl: HTMLElement) => {
    const shapeId = shapeWidgetEl.id;
    const topPx = parseFloat(shapeWidgetEl.style.top);
    const leftPx = parseFloat(shapeWidgetEl.style.left);
    const heightPx = parseFloat(shapeWidgetEl.style.height);
    const widthPx = parseFloat(shapeWidgetEl.style.width);
    const shapeSize = { topPx, widthPx, heightPx, leftPx };

    const textWidgetsWrappedByShape: HTMLElement[] = getWidgetsWrappedByShape(allTextWidgets, shapeSize);

    let shapeFixedWidgets: string[] = [];
    const isFixedText =
      widthPx === heightPx &&
      textWidgetsWrappedByShape.length === 1 &&
      textWidgetsWrappedByShape?.length !== allTextWidgets.length;
    const fixedWidgetsWrappedByShape: HTMLElement[] = isFixedText
      ? textWidgetsWrappedByShape
      : getWidgetsWrappedByShape(fixedWidgets, shapeSize);

    // set the background shape widget if it wraps all the text and fixed widgets
    if (
      !bgShapeWidgetEl &&
      textWidgetsWrappedByShape?.length === allTextWidgets.length &&
      fixedWidgetsWrappedByShape.length === fixedWidgets.length
    ) {
      bgShapeWidgetEl = shapeWidgetEl;
      bgShapeId = shapeId;
      bgShapeSize = shapeSize;
      return;
    }

    // Update the fixedWidgets contained by this shape
    // Ignore if the shape also wraps a non-fixed text widget
    if (isFixedText || (fixedWidgetsWrappedByShape?.length && !textWidgetsWrappedByShape?.length)) {
      shapeFixedWidgets = fixedWidgetsWrappedByShape.map((widget) => widget.id);
      const { topPadding, bottomPadding, leftPadding, rightPadding } = findShapeWidgetPadding(
        fixedWidgetsWrappedByShape,
        shapeSize,
      );

      // In case we found a fixed text widget wrapped by the shape, we need to add it to the fixedWidgetsMap
      const fixedWidgetId = shapeFixedWidgets[0];
      const fixedWidgetEl = fixedWidgetsWrappedByShape[0];
      if (!fixedWidgetsMap[fixedWidgetId]) {
        const { left, right } = getAdjacentWidgets(fixedWidgetsWrappedByShape[0], allTextWidgets);
        let paddingLeftPct = 0;
        if (left.length === 0 && right.length === 0) {
          const paddingLeftFromBg =
            parseFloat(fixedWidgetEl.style.left) - bgShapeSize.leftPx + fixedWidgetEl.clientWidth / 2;
          paddingLeftPct = Math.max(paddingLeftFromBg, 0) / (bgShapeSize.widthPx || 1);
        }

        fixedWidgetsMap[fixedWidgetId] = {
          id: fixedWidgetId,
          el: fixedWidgetsWrappedByShape[0],
          leftWidgets: left,
          rightWidgets: right,
          originalWidth: fixedWidgetsWrappedByShape[0].clientWidth,
          paddingLeftPct,
        };
      }

      cellShapeWidgets[shapeId] = {
        shapeId,
        shapeEl: shapeWidgetEl,
        bottomPadding,
        topPadding,
        leftPadding,
        rightPadding,
        fixedWidgets: shapeFixedWidgets,
        textWidgets: [],
      };
      return;
    }

    if (!textWidgetsWrappedByShape?.length) return;
    const sortedTextWidgetsByTop = sortByTopPxInPlace(textWidgetsWrappedByShape);
    const { topPadding, bottomPadding, leftPadding, rightPadding } = findShapeWidgetPadding(
      sortedTextWidgetsByTop,
      shapeSize,
    );

    cellShapeWidgets[shapeId] = {
      shapeId,
      shapeEl: shapeWidgetEl,
      bottomPadding,
      topPadding,
      leftPadding,
      rightPadding,
      fixedWidgets: shapeFixedWidgets,
      textWidgets: textWidgetsWrappedByShape.map((textWidgetEl: HTMLElement) => textWidgetEl.id),
    };
  });

  return { bgShapeId, bgShapeEl: bgShapeWidgetEl, bgShapeSize, cellShapeWidgets };
};

/**
 * Populates the provided textWidgets and shapeWidgets arrays with the text and shape widgets
 * of the target in the provided OnResizeStartEvent.
 *
 * @param {OnResizeStartEvent} ev - The resize start event containing the target widget.
 * @param {HTMLElement[]} textWidgets - The array to populate with text widgets.
 * @param {HTMLElement[]} shapeWidgets - The array to populate with basic shape widgets.
 *
 * @returns {void}
 */
export const createResponsiveWidgetArr = (
  ev: OnResizeStartEvent,
  textWidgets: HTMLElement[],
  shapeWidgets: HTMLElement[],
  graphicWidgets: HTMLElement[],
) => {
  const widgetId = ev.target.id;
  const widgetType = getWidgetTypeFromId(widgetId);

  const isTextWidget = widgetType === WidgetType.Text;
  const isBasicShapeWidget = widgetType === WidgetType.BasicShape;
  const isGraphicWidget = [WidgetType.Image, WidgetType.Icon, WidgetType.LineIcon].includes(widgetType);

  if (isTextWidget) textWidgets.push(ev.target as HTMLElement);
  if (isBasicShapeWidget) shapeWidgets.push(ev.target as HTMLElement);
  if (isGraphicWidget) graphicWidgets.push(ev.target as HTMLElement);
};

/**
 * Helper to check if the current active group is a valid responsive group.
 * Used to determine whether to show side handles
 *
 * Criteria:
 * - FALSE if any rotated widgets
 * - TRUE if only shapes and text in the group
 *
 * Otherwise, the group has a fixed widget and must meet the following criteria:
 * - Max 10 members (to optimize layout checks)
 * - Max 3 text widgets
 * - Allows 1 fixed widget (icon/image) - optional
 * - MUST HAVE 1 background shape
 * - Allows max 1 column beside a fixed widget
 * - Does not allow overlap between text and fixed widget
 *
 * Allowed:
 * - [FIXED] [TEXT]
 * - [TEXT] [FIXED]
 *
 * Not allowed:
 * - [TEXT] [TEXT] [FIXED]
 * - [TEXT] [FIXED] [TEXT]
 *
 * @param activeWidgets
 * @returns
 */
export const checkIsValidResponsiveGroup = (activeWidgets: ActiveWidgets): boolean => {
  const groupWidget = activeWidgets[0];
  if (!groupWidget || !groupWidget.groupId) {
    return false;
  }
  let numText = 0;
  let numShapes = 0;

  // icon and image
  let numGraphic = 0;
  let hasSideHandles = true;

  const textWidgets: (WidgetPosition & { id: string })[] = [];
  const graphicWidgets: (WidgetPosition & { id: string })[] = [];

  const storeState = store.getState();
  const widgets = selectWidgets(storeState);
  const { groupMembers } = groupWidget;
  for (let i = 0; i < groupMembers.length; i++) {
    const memberId = groupMembers[i];
    const memberType = getWidgetTypeFromId(memberId);

    const memberWidgetData = widgets[memberId];

    const widgetPos = {
      id: memberId,
      topPx: memberWidgetData?.topPx || 0,
      leftPx: memberWidgetData?.leftPx || 0,
      widthPx: memberWidgetData?.widthPx || 0,
      heightPx: memberWidgetData?.heightPx || 0,
      rotateDeg: memberWidgetData?.rotateDeg || 0,
    };

    // Don't allow rotated widgets in group
    if (widgetPos.rotateDeg !== 0) {
      hasSideHandles = false;
      break;
    }

    // Check for supported widgets only
    if (memberType === WidgetType.Text) {
      numText++;
      textWidgets.push(widgetPos);
    } else if (memberType === WidgetType.BasicShape) {
      numShapes++;
    } else if ([WidgetType.Icon, WidgetType.LineIcon, WidgetType.Image].includes(memberType)) {
      numGraphic++;
      graphicWidgets.push(widgetPos);
    } else {
      // Unsupported widget
      hasSideHandles = false;
      break;
    }

    // Only allow 1 graphic widget
    if (numGraphic > MAX_NUM_GRAPHIC) {
      hasSideHandles = false;
      break;
    }

    // If a graphic widget is present, limit the number of text widgets
    if (numGraphic === MAX_NUM_GRAPHIC) {
      if (numText > MAX_NUM_TEXT) {
        hasSideHandles = false;
        break;
      }
    }
  }

  if (!hasSideHandles) return false;
  // Must have at least 1 text widget
  if (numText === 0) return false;

  // Only shapes and text in the group - should have side handles
  if (numGraphic === 0) return true;

  // Must have at least 1 shape widget (that is the bg)
  if (numShapes === 0) return false;

  // Check max num members (only if fixed widget is present)
  if (groupMembers.length > MAX_NUM_MEMBERS) return false;

  ////// Check layout
  const fixedWidgetPos = graphicWidgets[0];

  const { topPx: fixedTopPx, leftPx: fixedLeftPx, widthPx: fixedWidthPx, heightPx: fixedHeightPx } = fixedWidgetPos;
  const fixedRightPx = fixedLeftPx + fixedWidthPx;
  const fixedBottom = fixedTopPx + fixedHeightPx;
  sortByLeftPxInPlace(textWidgets);

  let leftWidgetsCt = 0;
  let rightWidgetsCt = 0;
  let smallestRightPx = Infinity;
  for (const textWidget of textWidgets) {
    const { leftPx, widthPx, topPx, heightPx } = textWidget;
    const rightPx = leftPx + widthPx;
    const bottomPx = topPx + heightPx;

    let isLeft = false;
    let isRight = false;
    if (rightPx <= fixedLeftPx) {
      leftWidgetsCt++;
      isLeft = true;
    }

    if (leftPx >= fixedRightPx) {
      rightWidgetsCt++;
      isRight = true;
    }

    // Check overlap - if overlapping, not a valid responsive group
    if (!(leftPx >= fixedRightPx || rightPx <= fixedLeftPx || topPx >= fixedBottom || bottomPx <= fixedTopPx)) {
      hasSideHandles = false;
      break;
    }

    // Can't have text on both sides of fixed
    if (leftWidgetsCt > 0 && rightWidgetsCt > 0) {
      hasSideHandles = false;
      break;
    }

    // Found 2 columns in the left if we encounter another left widget
    if (leftWidgetsCt > 1 && isLeft) {
      if (leftPx > smallestRightPx) {
        hasSideHandles = false;
        break;
      }
    }

    // Found 2 columns in the right if we encounter another right widget
    if (rightWidgetsCt > 1 && isRight) {
      if (leftPx > smallestRightPx) {
        hasSideHandles = false;
        break;
      }
    }

    smallestRightPx = Math.min(smallestRightPx, rightPx);
  }

  return hasSideHandles;
};
